/**
 * CategoryNormalizer — Normalisation / reclassification des catégories
 *
 * Problème : les services partenaires (Zalando, Amazon, ASOS...) retournent
 * souvent des articles avec une catégorie mal attribuée :
 *   - Une "jupe" dans "Robes"
 *   - Un "t-shirt" dans "Vestes"
 *   - Un "sac à main" dans "Accessoires"
 *
 * Solution : on regarde le nom (et la description si dispo) pour détecter
 * la vraie catégorie via des mots-clés précis. Cette fonction retourne
 * la catégorie corrigée ou la catégorie d'origine si le nom ne donne
 * aucun indice clair.
 */

// Patterns de détection par catégorie (mots entiers uniquement avec \b)
// L'ordre importe : on teste d'abord les plus spécifiques (Robes avant Bas pour ne pas confondre)
// ⚠️ Ordre cohérent avec GenderFilter.CATEGORY_DETECTORS pour éviter les
// désaccords entre les 2 détecteurs (qui causaient le rejet de 86+ articles
// légitimes lors d'un appel à rejectMiscategorized).
const CAT_PATTERNS = [
  // Chaussures (ultra-spécifique d'abord)
  { cat: 'Chaussures', regex: /\b(chaussure|chaussures|shoe|shoes|sneaker|sneakers|basket|baskets|boot|boots|bottine|bottines|botte|bottes|mocassin|mocassins|sandale|sandales|escarpin|escarpins|ballerine|ballerines|tong|tongs|espadrille|espadrilles|derby|derbies|richelieu|slipper|slippers|loafer|loafers)\b/i },

  // Sacs
  { cat: 'Sacs', regex: /\b(sac|sacs|bag|bags|handbag|handbags|pochette|pochettes|sacoche|sacoches|cabas|backpack|backpacks|tote|totebag|clutch|clutches|besace)\b/i },

  // Bijoux
  { cat: 'Bijoux', regex: /\b(collier|colliers|bracelet|bracelets|boucle|boucles d'oreille|bague|bagues|pendentif|pendentifs|cha[îi]nette|jewelry|bijou|bijoux|earring|earrings|necklace|necklaces|ring|rings)\b/i },

  // v1.12.294 — Maillots (testé AVANT Robes pour ne pas capter "robe de bain" / Bas pour bermuda de bain)
  // v1.12.308 — pareo / sarong / caftan / cover-up ajoutés
  { cat: 'Maillots', regex: /\b(maillot|maillots|maillot\s*de\s*bain|bikini|bikinis|monokini|tankini|trikini|swimsuit|swim\s*suit|swimwear|swimming|bain|swim\s*shorts|swimshorts|swim\s*trunk|swim\s*trunks|swim\s*brief|swim\s*briefs|board\s*shorts|boardshorts|burkini|pareo|paréo|sarong|caftan|kaftan|beach\s*robe|beach\s*dress|cover[-\s]?up)\b/i },
  // Robes — mots très spécifiques
  { cat: 'Robes', regex: /\b(robe|robes|dress|jumpsuit|combinaison|playsuit)\b/i },

  // v1.12.222 — Manteaux SÉPARÉS de Vestes (testés AVANT pour priorité)
  { cat: 'Manteaux', regex: /\b(manteau|manteaux|trench|trenchs|parka|parkas|doudoune|doudounes|anorak|anoraks|caban|cabans|peacoat|peacoats|pardessus|overcoat|overcoats|topcoat|topcoats|duffle\s*coat|dufflecoat|raincoat|raincoats|imperm[eé]able|imperm[eé]ables|long\s*coat|wool\s*coat)\b/i },

  // Vestes — blazers, cardigans, bombers, gilets (sans les manteaux/parkas/trenchs)
  { cat: 'Vestes', regex: /\b(veste|vestes|blazer|blazers|cardigan|cardigans|bomber|bombers|jacket|jackets|gilet|gilets|kimono|kimonos|teddy|teddys|perfecto|perfectos|biker|bikers)\b/i },

  // Hauts — t-shirts, chemises, pulls (avant Bas pour "Chemise Denim")
  { cat: 'Hauts', regex: /\b(t-?shirt|t-?shirts|tshirt|tshirts|top|tops|blouse|blouses|chemise|chemises|shirt|shirts|pull|pulls|pullover|pullovers|sweater|sweaters|sweatshirt|sweatshirts|hoodie|hoodies|polo|polos|tunique|tuniques|d[eé]bardeur|d[eé]bardeurs|body|bodys|bodies|camisole|camisoles|brassi[eè]re|brassi[eè]res)\b/i },

  // Bas — jupes, pantalons, shorts (en dernier des vêtements)
  // ⚠️ Pas de "slim" : adjectif de fit, faux positif sur "Chemise Slim"
  { cat: 'Bas', regex: /\b(jupe|jupes|skirt|skirts|pantalon|pantalons|jean|jeans|chino|chinos|short|shorts|bermuda|bermudas|legging|leggings|jogging|joggers|cargo|pants|trousers)\b/i },

  // Accessoires — doit être après Sacs/Bijoux pour ne pas tout capturer
  { cat: 'Accessoires', regex: /\b(ceinture|ceintures|belt|belts|foulard|foulards|[eé]charpe|[eé]charpes|scarf|scarves|lunette|lunettes|sunglasses|chapeau|chapeaux|hat|hats|casquette|casquettes|cap|caps|bonnet|bonnets|beanie|gant|gants|glove|gloves|montre|montres|watch|cravate|cravates|tie|ties|bretelle|bretelles|pochette carrée|noeud)\b/i },
];

/**
 * Détecte la catégorie d'un article à partir de son nom/titre.
 * @param {Object} product — avec au moins {name} ou {title}
 * @returns {string|null} catégorie détectée ou null si rien ne matche
 */
export function detectCategoryFromName(product) {
  if (!product) return null;
  const text = [
    product.name || '',
    product.title || '',
    product.description || '',
  ].join(' ');
  if (!text.trim()) return null;

  for (const { cat, regex } of CAT_PATTERNS) {
    if (regex.test(text)) return cat;
  }
  return null;
}

/**
 * Normalise la catégorie d'un produit : si détection par nom donne une catégorie
 * différente de celle affichée, on préfère la détection (plus fiable que le partner).
 *
 * Règle : on corrige UNIQUEMENT si la détection par nom donne un résultat clair.
 * Si rien ne matche → on garde la catégorie d'origine (peut être correcte si le
 * nom est vague ou en langue étrangère).
 *
 * @param {Object} product
 * @returns {Object} produit avec catégorie corrigée si besoin
 */
export function normalizeCategory(product) {
  if (!product) return product;
  const detected = detectCategoryFromName(product);
  if (!detected) return product; // rien détecté → laisser tel quel
  if (detected === product.category) return product; // déjà correct
  // Correction : on remplace la catégorie par celle détectée
  return { ...product, category: detected, _originalCategory: product.category };
}

/**
 * Applique la normalisation à un tableau de produits.
 * @param {Array} products
 * @returns {Array}
 */
export function normalizeCategoriesBatch(products) {
  if (!Array.isArray(products)) return [];
  return products.map(normalizeCategory);
}

/**
 * Filtre + reclassification : garde uniquement les produits qui correspondent
 * à la catégorie demandée. Les produits mal classés par le partner mais dont
 * le nom suggère une autre catégorie sont rejetés.
 *
 * Exemple : un article dans "Robes" avec le nom "Jupe plissée" sera rejeté
 *           car detectCategoryFromName renvoie "Bas".
 *
 * @param {Array} products
 * @param {string} targetCategory — 'Robes', 'Hauts', etc.
 * @returns {Array}
 */
export function filterByCategoryStrict(products, targetCategory) {
  if (!Array.isArray(products)) return [];
  if (!targetCategory || targetCategory === 'Tout') return products;

  return products.filter(p => {
    if (p.category !== targetCategory) return false;
    // On détecte à partir du nom
    const detected = detectCategoryFromName(p);
    // Si aucune détection → on garde (nom vague/étranger)
    if (!detected) return true;
    // Si la détection correspond à la catégorie cible → OK
    if (detected === targetCategory) return true;
    // Détection différente → rejet (mal classé par le partner)
    return false;
  });
}
